import { useMemo } from "react";

interface Props {
  text: string;
  className?: string;
  /** When true, render as serif (matches the doc prose). Defaults to false. */
  serif?: boolean;
}

/**
 * Lightweight synchronous Markdown renderer for chat messages.
 *
 * Streaming tokens arrive every ~20-50ms so we cannot afford the async
 * MDAST pipeline (`mdToHtml`) here. This is a regex-based renderer that
 * covers what the chat answer actually emits:
 *
 *   # ## ### headings    →  h1..h6
 *   **bold** __bold__    →  <strong>
 *   *italic* _italic_    →  <em>
 *   `code`               →  <code>
 *   ```fenced```         →  <pre><code>
 *   - / * / + items      →  <ul><li>
 *   1. numbered          →  <ol><li>
 *   > quote              →  <blockquote>
 *   ---                  →  <hr>
 *   [text](url)          →  <a>
 *   newline twice        →  paragraph break
 *
 * Plus `{{src:sec-id}}` tokens that the agent uses for citations are
 * passed through unchanged (the parent maps them to clickable chips).
 *
 * For full Markdown (tables, footnotes, autolinks complex) use mdToHtml
 * once the message is finalised (msg.streaming === false).
 */
export function ChatMessageMarkdown({ text, className, serif }: Props) {
  const html = useMemo(() => renderMd(text), [text]);
  return (
    <div
      data-testid="chat-message-md"
      className={`chat-md ${serif ? "chat-md-serif" : ""} ${className ?? ""}`}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: input is escaped before any HTML emission inside renderMd
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderInline(input: string): string {
  // Order matters: code first (inside code we don't process other md).
  // Input arrives ALREADY html-escaped (caller does escapeHtml on the
  // whole paragraph before this). So inside backticks we only need to
  // wrap the (already-escaped) text in <code>…</code>; double-escaping
  // would produce &amp;lt; instead of &lt;.
  let s = input;
  // 1. Inline code  `…`
  s = s.replace(/`([^`\n]+)`/g, (_, code) => `<code>${code}</code>`);
  // 2. Bold + italic ***x***
  s = s.replace(/\*\*\*([^*\n]+)\*\*\*/g, "<strong><em>$1</em></strong>");
  // 3. Bold **x**, __x__
  s = s.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  // 4. Italic *x*, _x_  (avoid eating already-bold)
  s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  s = s.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, "$1<em>$2</em>");
  // 5. Links [text](url)
  s = s.replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, (_, label, url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  return s;
}

/**
 * Pure markdown → HTML transform used by `ChatMessageMarkdown`.
 *
 * Exported for unit tests; no React dependency. Keep it sync and cheap
 * (regex-based) since it runs on every streamed token.
 */
export function renderMd(source: string): string {
  if (!source) return "";
  // Tokenize line-by-line, build block HTML.
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const out: string[] = [];

  let i = 0;
  let inList: "ul" | "ol" | null = null;
  let inQuote = false;

  function closeList() {
    if (inList) {
      out.push(`</${inList}>`);
      inList = null;
    }
  }
  function closeQuote() {
    if (inQuote) {
      out.push("</blockquote>");
      inQuote = false;
    }
  }

  while (i < lines.length) {
    const raw = lines[i] ?? "";
    const line = raw.trimEnd();

    // Fenced code block ```lang
    const fence = /^```\s*([\w-]*)\s*$/.exec(line);
    if (fence) {
      closeList();
      closeQuote();
      const lang = fence[1] ?? "";
      const codeLines: string[] = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i] ?? "")) {
        codeLines.push(lines[i] ?? "");
        i += 1;
      }
      i += 1;
      const langAttr = lang ? ` data-lang="${escapeHtml(lang)}"` : "";
      out.push(`<pre${langAttr}><code>${escapeHtml(codeLines.join("\n"))}</code></pre>`);
      continue;
    }

    // Heading  # → h1, ## → h2, …
    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      closeList();
      closeQuote();
      const level = heading[1]?.length ?? 1;
      const text = renderInline(escapeHtml(heading[2] ?? ""));
      out.push(`<h${level}>${text}</h${level}>`);
      i += 1;
      continue;
    }

    // Horizontal rule
    if (/^([-*_])\s*\1\s*\1[\s\S]*$/.test(line) && line.replace(/\s/g, "").length >= 3) {
      closeList();
      closeQuote();
      out.push("<hr/>");
      i += 1;
      continue;
    }

    // Blockquote
    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      closeList();
      if (!inQuote) {
        out.push("<blockquote>");
        inQuote = true;
      }
      out.push(`<p>${renderInline(escapeHtml(quote[1] ?? ""))}</p>`);
      i += 1;
      continue;
    }
    closeQuote();

    // Unordered list
    const ul = /^[-*+]\s+(.+)$/.exec(line);
    if (ul) {
      if (inList !== "ul") {
        closeList();
        out.push("<ul>");
        inList = "ul";
      }
      out.push(`<li>${renderInline(escapeHtml(ul[1] ?? ""))}</li>`);
      i += 1;
      continue;
    }

    // Ordered list
    const ol = /^\d+\.\s+(.+)$/.exec(line);
    if (ol) {
      if (inList !== "ol") {
        closeList();
        out.push("<ol>");
        inList = "ol";
      }
      out.push(`<li>${renderInline(escapeHtml(ol[1] ?? ""))}</li>`);
      i += 1;
      continue;
    }
    closeList();

    // Empty line — paragraph break
    if (line.trim() === "") {
      i += 1;
      continue;
    }

    // Paragraph (collapse consecutive non-blank lines into one <p>)
    const para: string[] = [line];
    let j = i + 1;
    while (j < lines.length) {
      const nxt = lines[j] ?? "";
      if (nxt.trim() === "") break;
      if (/^(#{1,6}\s|>\s|[-*+]\s|\d+\.\s|```)/.test(nxt)) break;
      para.push(nxt);
      j += 1;
    }
    out.push(`<p>${renderInline(escapeHtml(para.join(" ")))}</p>`);
    i = j;
  }

  closeList();
  closeQuote();
  return out.join("");
}
